import { type MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ArrowLeft, Edit3, Loader2, Music, Pause, Play, RefreshCw, SkipForward, Volume2, VolumeX, X } from 'lucide-react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  updateChannel,
  updateChannelGroups,
  updateChannelLabelRules,
  uploadChannelCover,
  getAudioGroups,
  getAudioImageUrl,
  getChannelPlayground,
  getNextChannelPlaygroundAudioTrack,
  resolveMediaUrl,
  type AudioGroup,
  type Channel,
  type ChannelPlayground,
  type ChannelPlaygroundTrack,
  type PublicAudioTrack,
} from '../api'
import {
  getLabels,
  type Label
} from '../api/labels'
import {
  getVisualGroups,
  type VisualGroup
} from '../api/visuals'
import type { ChannelColorThemeId } from '../channelColorThemes'
import { useToast } from '../components/ToastProvider'
import { Button, DataTableElement, DataTableSurface, EmptyState, IconButton, TablePagination, adminMediaPlayerClassName } from '../components/ui'
import { formatBpm } from '../features/audio/audioFormatting'
import { ChannelDialog } from '../features/channels/ChannelDialog'
import { formatDuration } from '../features/visuals/formatDuration'
import styles from './ChannelPlaygroundPage.module.css'

type PlayerMode = 'channel' | 'track'

const playgroundTrackPageSizeOptions = [10, 25, 50]

const cx = (...classNames: Array<string | false | null | undefined>): string =>
  classNames
    .filter(Boolean)
    .map((className) => styles[className as string] ?? className)
    .join(' ')

const pc = adminMediaPlayerClassName

const getRepeatWindowSize = (readyAudioCount: number): number =>
  Math.min(5, Math.max(0, readyAudioCount - 1))

const formatBpmRange = (channel: Channel): string => {
  if (channel.bpm_min == null && channel.bpm_max == null) {
    return 'Any BPM'
  }

  if (channel.bpm_min != null && channel.bpm_max != null) {
    return `${Math.round(channel.bpm_min)}-${Math.round(channel.bpm_max)} BPM`
  }

  return channel.bpm_min != null
    ? `From ${Math.round(channel.bpm_min)} BPM`
    : `Up to ${Math.round(channel.bpm_max!)} BPM`
}

const labelSummary = (labels: Array<{ name: string }>): string =>
  labels.map((label) => label.name).join(', ')

const TrackReasons = ({ track }: { track: ChannelPlaygroundTrack }) => {
  const { match_reasons: reasons } = track
  const hasReasons = reasons.audio_groups.length > 0
    || reasons.include_labels.length > 0
    || reasons.boost_labels.length > 0
    || reasons.bpm

  if (!hasReasons) {
    return <span className={cx('channel-playground-muted')}>Ready track</span>
  }

  return (
    <div className={cx('channel-playground-reasons')}>
      {reasons.audio_groups.length > 0 && (
        <span>Groups: {labelSummary(reasons.audio_groups)}</span>
      )}
      {reasons.include_labels.length > 0 && (
        <span>Include: {labelSummary(reasons.include_labels)}</span>
      )}
      {reasons.boost_labels.length > 0 && (
        <span>
          Boost: {reasons.boost_labels.map((label) => `${label.name} x${Number(label.weight).toFixed(1)}`).join(', ')}
        </span>
      )}
      {reasons.bpm && (
        <span>
          BPM: {Math.round(reasons.bpm.value)}
          {reasons.bpm.min != null || reasons.bpm.max != null
            ? ` within ${reasons.bpm.min ?? 'min'}-${reasons.bpm.max ?? 'max'}`
            : ''}
        </span>
      )}
    </div>
  )
}

const usePlaygroundPlayer = (channelId: string, readyAudioCount: number) => {
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const currentTrackRef = useRef<PublicAudioTrack | null>(null)
  const modeRef = useRef<PlayerMode>('track')
  const recentTrackIdsRef = useRef<string[]>([])

  const [currentTrack, setCurrentTrack] = useState<PublicAudioTrack | null>(null)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [loadingTrackId, setLoadingTrackId] = useState<string | null>(null)
  const [isMuted, setIsMuted] = useState(false)
  const [isPlaying, setIsPlaying] = useState(false)
  const [mode, setMode] = useState<PlayerMode>('track')
  const [volume, setVolumeState] = useState(0.8)

  const rememberTrack = useCallback((trackId: string): void => {
    const windowSize = getRepeatWindowSize(readyAudioCount)
    recentTrackIdsRef.current = windowSize > 0
      ? [...recentTrackIdsRef.current, trackId].slice(-windowSize)
      : []
  }, [readyAudioCount])

  const loadTrack = useCallback(async (track: PublicAudioTrack, nextMode: PlayerMode): Promise<void> => {
    const audio = audioRef.current

    if (!audio) {
      return
    }

    modeRef.current = nextMode
    currentTrackRef.current = track
    setMode(nextMode)
    setCurrentTrack(track)
    setCurrentTime(0)
    setDuration(track.duration_seconds ?? 0)
    setErrorMessage(null)

    audio.src = resolveMediaUrl(track.file_url)
    await audio.play()
  }, [])

  const playNextChannelTrack = useCallback(async (): Promise<void> => {
    setIsLoading(true)
    setLoadingTrackId(null)
    setErrorMessage(null)

    try {
      const track = await getNextChannelPlaygroundAudioTrack(channelId, {
        excludeTrackIds: recentTrackIdsRef.current,
        currentTrackId: currentTrackRef.current?.id ?? null
      })

      rememberTrack(track.id)
      await loadTrack(track, 'channel')
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Unable to play channel stream')
    } finally {
      setIsLoading(false)
    }
  }, [channelId, loadTrack, rememberTrack])

  useEffect(() => {
    const audio = new Audio()
    audio.preload = 'auto'
    audio.volume = volume
    audioRef.current = audio

    const handleEnded = (): void => {
      if (modeRef.current === 'channel') {
        void playNextChannelTrack()
      } else {
        setIsPlaying(false)
      }
    }
    const handleLoadedMetadata = (): void => {
      setDuration(Number.isFinite(audio.duration) ? audio.duration : currentTrackRef.current?.duration_seconds ?? 0)
    }
    const handlePause = (): void => setIsPlaying(false)
    const handlePlay = (): void => setIsPlaying(true)
    const handleTimeUpdate = (): void => setCurrentTime(audio.currentTime)

    audio.addEventListener('ended', handleEnded)
    audio.addEventListener('loadedmetadata', handleLoadedMetadata)
    audio.addEventListener('pause', handlePause)
    audio.addEventListener('play', handlePlay)
    audio.addEventListener('timeupdate', handleTimeUpdate)

    return () => {
      audio.pause()
      audio.removeEventListener('ended', handleEnded)
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata)
      audio.removeEventListener('pause', handlePause)
      audio.removeEventListener('play', handlePlay)
      audio.removeEventListener('timeupdate', handleTimeUpdate)
      audioRef.current = null
    }
  }, [playNextChannelTrack])

  const togglePlayback = useCallback((): void => {
    const audio = audioRef.current

    if (!audio || !currentTrackRef.current) {
      return
    }

    if (audio.paused) {
      void audio.play().catch((error) => {
        setErrorMessage(error instanceof Error ? error.message : 'Unable to resume playback')
      })
    } else {
      audio.pause()
    }
  }, [])

  const playTrack = useCallback(async (track: PublicAudioTrack): Promise<void> => {
    if (currentTrackRef.current?.id === track.id && modeRef.current === 'track') {
      togglePlayback()
      return
    }

    setIsLoading(true)
    setLoadingTrackId(track.id)
    recentTrackIdsRef.current = []

    try {
      await loadTrack(track, 'track')
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Unable to play track')
    } finally {
      setIsLoading(false)
      setLoadingTrackId(null)
    }
  }, [loadTrack, togglePlayback])

  const startChannel = useCallback(async (): Promise<void> => {
    recentTrackIdsRef.current = []
    await playNextChannelTrack()
  }, [playNextChannelTrack])

  const seek = useCallback((time: number): void => {
    const audio = audioRef.current

    if (!audio || !Number.isFinite(time)) {
      return
    }

    const nextTime = Math.max(0, Math.min(time, Number.isFinite(audio.duration) ? audio.duration : time))
    audio.currentTime = nextTime
    setCurrentTime(nextTime)
  }, [])

  const setVolume = useCallback((nextVolume: number): void => {
    const normalizedVolume = Math.max(0, Math.min(1, nextVolume))
    setVolumeState(normalizedVolume)

    if (audioRef.current) {
      audioRef.current.volume = normalizedVolume
    }
  }, [])

  const toggleMute = useCallback((): void => {
    setIsMuted((muted) => {
      const nextMuted = !muted

      if (audioRef.current) {
        audioRef.current.muted = nextMuted
      }

      return nextMuted
    })
  }, [])

  const stop = useCallback((): void => {
    const audio = audioRef.current

    if (audio) {
      audio.pause()
      audio.removeAttribute('src')
      audio.load()
    }

    currentTrackRef.current = null
    recentTrackIdsRef.current = []
    setCurrentTrack(null)
    setCurrentTime(0)
    setDuration(0)
    setErrorMessage(null)
    setIsPlaying(false)
    setLoadingTrackId(null)
  }, [])

  return {
    currentTime,
    currentTrack,
    duration,
    errorMessage,
    isLoading,
    loadingTrackId,
    isMuted,
    isPlaying,
    mode,
    playNextChannelTrack,
    playTrack,
    seek,
    setVolume,
    startChannel,
    stop,
    toggleMute,
    togglePlayback,
    volume
  }
}

const ChannelPlaygroundPlayer = ({
  currentTime,
  duration,
  isLoading,
  isMuted,
  isPlaying,
  mode,
  onNext,
  onSeek,
  onStop,
  onToggleMute,
  onTogglePlayback,
  onVolumeChange,
  track,
  volume
}: {
  currentTime: number
  duration: number
  isLoading: boolean
  isMuted: boolean
  isPlaying: boolean
  mode: PlayerMode
  onNext: () => void
  onSeek: (time: number) => void
  onStop: () => void
  onToggleMute: () => void
  onTogglePlayback: () => void
  onVolumeChange: (volume: number) => void
  track: PublicAudioTrack | null
  volume: number
}) => {
  const progressRef = useRef<HTMLDivElement | null>(null)
  const volumeRef = useRef<HTMLDivElement | null>(null)
  const progressRatio = duration > 0 ? Math.max(0, Math.min(1, currentTime / duration)) : 0
  const artworkUrl = track ? getAudioImageUrl(track, 'thumb') : null

  const seekTo = (event: MouseEvent<HTMLDivElement>): void => {
    if (!progressRef.current || duration <= 0) {
      return
    }

    const rect = progressRef.current.getBoundingClientRect()
    const ratio = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width))
    onSeek(ratio * duration)
  }

  const setVolumeTo = (event: MouseEvent<HTMLDivElement>): void => {
    if (!volumeRef.current) {
      return
    }

    const rect = volumeRef.current.getBoundingClientRect()
    onVolumeChange(Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width)))
  }

  return (
    <div className={pc('admin-audio-player', cx('channel-playground-player'))}>
      <div className={pc('admin-audio-player-track', styles.playerTrack)}>
        <div className={pc('admin-audio-player-thumb', artworkUrl && 'has-image')}>
          {artworkUrl ? <img alt="" src={resolveMediaUrl(artworkUrl)} /> : <Music size={18} />}
        </div>
        <div className={pc('admin-audio-player-info')}>
          <span className={cx('channel-playground-player-mode')}>
            {track ? (mode === 'channel' ? 'Channel stream' : 'Matched track') : 'Ready'}
          </span>
          <span className={pc('admin-audio-player-title')}>{track?.title ?? 'Choose a track or start channel stream'}</span>
          {track?.artist && <span className={pc('admin-audio-player-artist')}>{track.artist}</span>}
        </div>
      </div>

      <div className={pc('admin-audio-player-controls')}>
        <button
          aria-label={isLoading ? 'Loading track' : isPlaying ? 'Pause' : 'Play'}
          className={pc('admin-audio-player-btn', 'admin-audio-player-play-btn')}
          disabled={!track || isLoading}
          onClick={onTogglePlayback}
          type="button"
        >
          {isLoading ? <Loader2 className={pc('admin-audio-player-spinner')} size={16} /> : isPlaying ? <Pause size={16} /> : <Play size={16} />}
        </button>
        {mode === 'channel' && (
          <button
            aria-label="Next channel track"
            className={pc('admin-audio-player-btn')}
            disabled={isLoading}
            onClick={onNext}
            type="button"
          >
            <SkipForward size={15} />
          </button>
        )}
        <span className={pc('admin-audio-player-time')}>{formatDuration(Math.floor(currentTime))}</span>
        <div
          aria-label="Seek"
          aria-valuemax={100}
          aria-valuemin={0}
          aria-valuenow={Math.round(progressRatio * 100)}
          className={pc('admin-audio-player-progress')}
          onClick={seekTo}
          ref={progressRef}
          role="slider"
        >
          <div className={pc('admin-audio-player-progress-fill')} style={{ width: `${progressRatio * 100}%` }} />
        </div>
        <span className={pc('admin-audio-player-time')}>{formatDuration(Math.floor(duration))}</span>
      </div>

      <div className={pc('admin-audio-player-volume')}>
        <button className={pc('admin-audio-player-btn')} onClick={onToggleMute} type="button" aria-label={isMuted ? 'Unmute' : 'Mute'}>
          {isMuted ? <VolumeX size={15} /> : <Volume2 size={15} />}
        </button>
        <div
          aria-label="Volume"
          aria-valuemax={100}
          aria-valuemin={0}
          aria-valuenow={Math.round((isMuted ? 0 : volume) * 100)}
          className={pc('admin-audio-player-volume-bar')}
          onClick={setVolumeTo}
          ref={volumeRef}
          role="slider"
        >
          <div className={pc('admin-audio-player-volume-fill')} style={{ width: `${(isMuted ? 0 : volume) * 100}%` }} />
        </div>
        <button className={pc('admin-audio-player-btn', 'admin-audio-player-stop')} onClick={onStop} type="button" aria-label="Stop playback">
          <X size={15} />
        </button>
      </div>
    </div>
  )
}

export const ChannelPlaygroundPage = () => {
  const navigate = useNavigate()
  const { toast } = useToast()
  const { channelId = '' } = useParams()
  const [audioGroups, setAudioGroups] = useState<AudioGroup[]>([])
  const [labels, setLabels] = useState<Label[]>([])
  const [visualGroups, setVisualGroups] = useState<VisualGroup[]>([])
  const [playground, setPlayground] = useState<ChannelPlayground | null>(null)
  const [loadState, setLoadState] = useState<'loading' | 'ready' | 'error'>('loading')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [editingChannel, setEditingChannel] = useState<Channel | null>(null)

  const loadSupportData = useCallback(async (): Promise<void> => {
    const [audioGroupsPayload, labelsPayload, visualGroupsPayload] = await Promise.all([
      getAudioGroups(),
      getLabels({ status: 'active' }),
      getVisualGroups()
    ])

    setAudioGroups(audioGroupsPayload)
    setLabels(labelsPayload)
    setVisualGroups(visualGroupsPayload)
  }, [])

  useEffect(() => {
    void loadSupportData()
  }, [loadSupportData])
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)
  const player = usePlaygroundPlayer(channelId, playground?.tracks.length ?? 0)

  const loadPlayground = useCallback(async (): Promise<void> => {
    setLoadState('loading')
    setErrorMessage(null)

    try {
      setPlayground(await getChannelPlayground(channelId))
      setLoadState('ready')
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Unable to load channel playground')
      setLoadState('error')
    }
  }, [channelId])

  useEffect(() => {
    void loadPlayground()
  }, [loadPlayground])

  const channel = playground?.channel ?? null
  const tracks = playground?.tracks ?? []
  const totalPages = Math.max(1, Math.ceil(tracks.length / pageSize))
  const normalizedPage = Math.min(currentPage, totalPages)
  const firstItemNumber = tracks.length === 0 ? 0 : (normalizedPage - 1) * pageSize + 1
  const lastItemNumber = Math.min(normalizedPage * pageSize, tracks.length)
  const paginatedTracks = tracks.slice((normalizedPage - 1) * pageSize, normalizedPage * pageSize)
  const includeLabels = useMemo(
    () => channel?.label_rules.filter((rule) => rule.mode === 'include').map((rule) => rule.label.name) ?? [],
    [channel]
  )

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages)
    }
  }, [currentPage, totalPages])
  const excludeLabels = useMemo(
    () => channel?.label_rules.filter((rule) => rule.mode === 'exclude').map((rule) => rule.label.name) ?? [],
    [channel]
  )
  const boostLabels = useMemo(
    () => channel?.label_rules.filter((rule) => rule.mode === 'boost').map((rule) => `${rule.label.name} x${Number(rule.weight).toFixed(1)}`) ?? [],
    [channel]
  )

  const refresh = async (): Promise<void> => {
    await loadPlayground()
    toast('Channel playground refreshed.')
  }

  const handleSaveChannel = async (input: {
    id: string
    name: string
    slug: string
    description: string | null
    color_theme: ChannelColorThemeId
    is_active: boolean
    sort_order: number
    track_selection_mode: Channel['track_selection_mode']
    include_match_mode: Channel['include_match_mode']
    bpm_min: number | null
    bpm_max: number | null
    stream_bitrate: number
    stream_format: Channel['stream_format']
    source_quality: Channel['source_quality']
    visualGroupIds: string[]
    audioGroupIds: string[]
    labelRules: Array<{ labelId: string; mode: 'include' | 'exclude' | 'boost'; weight?: number }>
    coverFile: File | null
  }): Promise<void> => {
    const savedChannel = await updateChannel({
      id: input.id,
      name: input.name,
      slug: input.slug,
      description: input.description,
      color_theme: input.color_theme,
      is_active: input.is_active,
      track_selection_mode: input.track_selection_mode,
      include_match_mode: input.include_match_mode,
      bpm_min: input.bpm_min,
      bpm_max: input.bpm_max,
      sort_order: input.sort_order,
      stream_bitrate: input.stream_bitrate,
      stream_format: input.stream_format,
      source_quality: input.source_quality
    })

    await updateChannelGroups(savedChannel.id, input.visualGroupIds, input.audioGroupIds)
    await updateChannelLabelRules(savedChannel.id, input.labelRules)

    if (input.coverFile) {
      await uploadChannelCover(savedChannel.id, input.coverFile)
    }

    setEditingChannel(null)
    toast(`Updated channel "${input.name}".`)
    await loadPlayground()
  }

  if (loadState === 'loading') {
    return <main className={styles.routeLoading}>Loading channel playground...</main>
  }

  if (loadState === 'error' || !channel) {
    return (
      <div className="page-state">
        <h1 className="page-title">Channel Playground</h1>
        <p className="page-subtitle">{errorMessage ?? 'Channel not found.'}</p>
        <Button onClick={() => navigate('/admin/channels')}>
          <ArrowLeft size={16} /> Back to channels
        </Button>
      </div>
    )
  }

  return (
    <div className={cx('channel-playground-page')}>
      <div className={cx('page-header', 'channel-playground-header')}>
        <div>
          <Link className={cx('channel-playground-back')} to="/admin/channels">
            <ArrowLeft size={15} /> Channels
          </Link>
          <h1 className="page-title">{channel.name} Playground</h1>
          <p className="page-subtitle">
            Review matched tracks and listen through the pinned player.
          </p>
        </div>
        <div className="page-header-actions">
          <Button mobileFullWidth onClick={() => void player.startChannel()} variant="primary">
            <Play size={16} /> Play channel stream
          </Button>
          <Button mobileFullWidth onClick={() => void refresh()}>
            <RefreshCw size={16} /> Refresh
          </Button>
          <Button mobileFullWidth onClick={() => setEditingChannel(channel)}>
            <Edit3 size={16} /> Edit
          </Button>
        </div>
      </div>

      <section className={cx('channel-playground-summary')}>
        <div>
          <span>Selection</span>
          <strong>{channel.track_selection_mode === 'labels' ? `Labels (${channel.include_match_mode})` : 'Audio groups'}</strong>
        </div>
        <div>
          <span>Matched tracks</span>
          <strong>{tracks.length}</strong>
        </div>
        <div>
          <span>BPM rule</span>
          <strong>{formatBpmRange(channel)}</strong>
        </div>
        <div>
          <span>Status</span>
          <strong>{channel.is_active ? 'Active' : 'Inactive'}</strong>
        </div>
      </section>

      {channel.track_selection_mode === 'labels' && (
        <section className={cx('channel-playground-rules')}>
          <div>
            <span>Include</span>
            <strong>{includeLabels.length ? includeLabels.join(', ') : 'No include labels'}</strong>
          </div>
          <div>
            <span>Exclude</span>
            <strong>{excludeLabels.length ? excludeLabels.join(', ') : 'No exclude labels'}</strong>
          </div>
          <div>
            <span>Boost</span>
            <strong>{boostLabels.length ? boostLabels.join(', ') : 'No boost labels'}</strong>
          </div>
        </section>
      )}

      <DataTableSurface className={cx('channel-playground-table-card')} fullWidth mobileStack>
        <div className={cx('channel-playground-table-scroll')}>
          <DataTableElement>
            <thead>
              <tr>
                <th aria-label="Play track" className={cx('channel-playground-play-head')}></th>
                <th>Track</th>
                <th>BPM</th>
                <th>Why matched</th>
              </tr>
            </thead>
            <tbody>
              {paginatedTracks.map((track) => {
                const artworkUrl = getAudioImageUrl(track, 'thumb')
                const isCurrent = player.currentTrack?.id === track.id && player.mode === 'track'
                const isLoadingTrack = player.loadingTrackId === track.id

                return (
                  <tr key={track.id}>
                    <td className={cx('channel-playground-play-cell')} data-label="">
                      <IconButton
                        active={isCurrent}
                        disabled={isLoadingTrack}
                        icon={isLoadingTrack ? <span className="track-status-spinner" /> : isCurrent && player.isPlaying ? <Pause size={15} /> : <Play size={15} />}
                        iconSize="play"
                        label={isLoadingTrack ? `Loading ${track.title}` : isCurrent && player.isPlaying ? `Pause ${track.title}` : `Play ${track.title}`}
                        loading={isLoadingTrack}
                        onClick={() => void player.playTrack(track)}
                        title={isLoadingTrack ? 'Loading track' : isCurrent && player.isPlaying ? 'Pause track' : 'Play track'}
                      />
                    </td>
                    <td className={cx('channel-playground-track-cell')} data-label="">
                      <div className={pc('admin-audio-player-thumb', artworkUrl && 'has-image')}>
                        {artworkUrl ? <img alt="" src={resolveMediaUrl(artworkUrl)} loading="lazy" /> : <Music size={18} />}
                      </div>
                      <div>
                        <div className={styles.trackNameLine}>
                          <span className={styles.trackName}>{track.title}</span>
                        </div>
                        <div className={styles.trackDescription}>{track.artist ?? 'Unknown artist'} · {formatDuration(track.duration_seconds ?? 0)}</div>
                      </div>
                    </td>
                    <td className="channel-secondary-cell" data-label="BPM">{formatBpm(track.effective_bpm)}</td>
                    <td className={cx('channel-playground-reason-cell')} data-label="Why matched">
                      <TrackReasons track={track} />
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </DataTableElement>
          {tracks.length === 0 && (
            <EmptyState message="No ready tracks match this channel." />
          )}
        </div>
        <TablePagination
          ariaLabel="Matched track pagination"
          currentPage={normalizedPage}
          onNext={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
          onPrevious={() => setCurrentPage((page) => Math.max(1, page - 1))}
          pageSize={{
            onChange: (nextPageSize) => {
              setCurrentPage(1)
              setPageSize(nextPageSize)
            },
            options: playgroundTrackPageSizeOptions,
            value: pageSize
          }}
          summary={tracks.length === 0 ? 'No tracks' : `${firstItemNumber}-${lastItemNumber} of ${tracks.length}`}
          totalPages={totalPages}
        />
      </DataTableSurface>

      {player.errorMessage && (
        <div className={cx('channel-playground-player-error')} role="status">{player.errorMessage}</div>
      )}

      <ChannelPlaygroundPlayer
        currentTime={player.currentTime}
        duration={player.duration}
        isLoading={player.isLoading}
        isMuted={player.isMuted}
        isPlaying={player.isPlaying}
        mode={player.mode}
        onNext={() => void player.playNextChannelTrack()}
        onSeek={player.seek}
        onStop={player.stop}
        onToggleMute={player.toggleMute}
        onTogglePlayback={player.togglePlayback}
        onVolumeChange={player.setVolume}
        track={player.currentTrack}
        volume={player.volume}
      />
      {editingChannel && (
        <ChannelDialog
          audioGroups={audioGroups}
          channel={editingChannel}
          labels={labels}
          visualGroups={visualGroups}
          onClose={() => setEditingChannel(null)}
          onSave={handleSaveChannel}
        />
      )}
    </div>
  )
}
